import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import EntryForm from "@/components/EntryForm";
import EntriesTable from "@/components/EntriesTable";
import EditEntryDialog from "@/components/EditEntryDialog";
import ManageListsDialog from "@/components/ManageListsDialog";
import PdfPreviewDialog from "@/components/PdfPreviewDialog";
import BrickHeader from "@/components/BrickHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Download, LogOut, Clock, SlidersHorizontal } from "lucide-react";

export default function Dashboard() {
  const { logout } = useAuth();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [helpers, setHelpers] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const loadAll = useCallback(async () => {
    const [e, h, w] = await Promise.all([
      api.get("/entries"),
      api.get("/helpers"),
      api.get("/work-types"),
    ]);
    setEntries(e.data.entries);
    setTotal(e.data.total_hours);
    setHelpers(h.data);
    setWorkTypes(w.data);
  }, []);

  useEffect(() => {
    loadAll().catch(() => toast.error("Daten konnten nicht geladen werden."));
  }, [loadAll]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/entries/${id}`);
      toast.success("Eintrag gelöscht.");
      loadAll();
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setEditOpen(true);
  };

  const renameHelper = async (oldName, newName) => {
    try {
      await api.put("/helpers/rename", { old_name: oldName, new_name: newName });
      toast.success("Helfer umbenannt.");
      await loadAll();
    } catch {
      toast.error("Umbenennen fehlgeschlagen.");
    }
  };
  const deleteHelper = async (name) => {
    try {
      await api.delete(`/helpers/${encodeURIComponent(name)}`);
      toast.success("Helfer aus Liste entfernt.");
      await loadAll();
    } catch {
      toast.error("Entfernen fehlgeschlagen.");
    }
  };
  const renameWorkType = async (oldName, newName) => {
    try {
      await api.put("/work-types/rename", { old_name: oldName, new_name: newName });
      toast.success("Bauarbeit umbenannt.");
      await loadAll();
    } catch {
      toast.error("Umbenennen fehlgeschlagen.");
    }
  };
  const deleteWorkType = async (name) => {
    try {
      await api.delete(`/work-types/${encodeURIComponent(name)}`);
      toast.success("Bauarbeit aus Liste entfernt.");
      await loadAll();
    } catch {
      toast.error("Entfernen fehlgeschlagen.");
    }
  };

  const downloadPdf = () => {
    const token = localStorage.getItem("btb_token");
    if (!token) {
      toast.error("Bitte erneut anmelden.");
      return;
    }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/pdf?token=${encodeURIComponent(token)}`;
    const win = window.open(url, "_blank", "noopener");
    if (!win) {
      // Fallback if popups are blocked: same-tab navigation (browser handles the PDF)
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F4]" data-testid="dashboard-page">
      <BrickHeader
        right={
          <button onClick={logout} data-testid="logout-button"
            className="title-box inline-flex items-center gap-2 px-3 py-2 text-xs sm:text-sm hover:bg-[#1a1a1a] transition-colors">
            <LogOut size={14} /> <span className="hidden sm:inline">Abmelden</span>
          </button>
        }
      />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8">
        <EntryForm helpers={helpers} workTypes={workTypes} onCreated={loadAll} />

        <Card className="bg-white border-[#E2E2E2] rounded-xl overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 sm:p-6 border-b border-[#E2E2E2]">
            <div>
              <h2 className="font-heading text-xl sm:text-2xl text-[#E6017D]">Einträge</h2>
              <p className="text-sm text-[#5F6368] mt-1 flex items-center gap-2">
                <Clock size={15} /> Gesamtstunden:{" "}
                <span className="font-semibold text-[#E6017D]" data-testid="total-hours">{total}</span>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setManageOpen(true)} data-testid="manage-lists-button"
                className="h-11 px-5 bg-white border-[#E2E2E2] text-[#212529] hover:bg-[#FCE7F3] hover:text-[#E6017D]">
                <SlidersHorizontal size={16} className="mr-2" /> Listen verwalten
              </Button>
              <Button onClick={downloadPdf} data-testid="download-pdf-button"
                className="h-11 px-6 bg-[#E6017D] hover:bg-[#C4016A] text-white transition-colors">
                <Download size={18} className="mr-2" />
                PDF herunterladen
              </Button>
            </div>
          </div>
          <div className="p-1 sm:p-4">
            <EntriesTable entries={entries} onDelete={handleDelete} onEdit={handleEdit} />
          </div>
        </Card>
      </main>

      <EditEntryDialog
        entry={editingEntry}
        open={editOpen}
        onOpenChange={setEditOpen}
        helpers={helpers}
        workTypes={workTypes}
        onSaved={loadAll}
      />
      <ManageListsDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        helpers={helpers}
        workTypes={workTypes}
        onRenameHelper={renameHelper}
        onDeleteHelper={deleteHelper}
        onRenameWorkType={renameWorkType}
        onDeleteWorkType={deleteWorkType}
      />
    </div>
  );
}
